import { isNil } from 'lodash-es';
import { useEffect } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import type {
  OrderFormV3FieldValues,
  TaxRateScheduleOption,
} from '../OrderFormV3Context';

const toRateNumber = (rate: string | number | null | undefined) =>
  isNil(rate) || String(rate).trim() === '' ? null : Number(rate);

/**
 * Triggers: the watched `taxRateSchedule` autocomplete value (Provider watch —
 *           the typeahead stores the parsed schedule entity beside the
 *           option pair).
 * Writes:   `taxRate` — the selected schedule's rate as a number, or null when
 *           the schedule carries none. Dirtied: TaxPanel disables the manual
 *           input while a schedule is selected, making this the field's only
 *           writer, and deparseUpdateV3 emits `tax_rate` only for dirty
 *           fields — an undirtied write renders but never reaches the API.
 * Guards:   only runs when the selected option carries an attached entity —
 *           clearing the schedule (or the entity-less defaultValues seed)
 *           leaves a manual taxRate in place; the field re-enables instead.
 *           The rate must also differ from the current value, so saves that
 *           don't move it stay sparse.
 * Legacy:   the legacy form's tax-rate autopopulate effect, undirtied there
 *           because its full PATCH shipped `tax_rate` regardless.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("tax section").
 */
export function useTaxRateAutopopulate({
  watchedTaxRateSchedule,
  getValues,
  setValue,
}: {
  watchedTaxRateSchedule: TaxRateScheduleOption | null | undefined;
  getValues: UseFormGetValues<OrderFormV3FieldValues>;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  useEffect(() => {
    if (!watchedTaxRateSchedule?._entity) return;

    const scheduleRate = toRateNumber(watchedTaxRateSchedule._entity.rate);
    const currentTaxRate = toRateNumber(getValues('taxRate'));

    if (scheduleRate === currentTaxRate) return;

    setValue('taxRate', scheduleRate, { shouldDirty: true });
  }, [watchedTaxRateSchedule, getValues, setValue]);
}
